import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Icon, type IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/*
 * Figma component set "Icon button": round, 52 px, icon 24 px.
 *   primary  bg/brand → hover bg/brand-hover → disabled bg/muted (icon stays white)
 *   outline  white, border/subtle, Card hover shadow → hover black → disabled icon/disabled, no shadow
 * `s` (40 px) is the compact size for mobile layouts.
 */
const iconButtonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center rounded-full transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2',
    'disabled:pointer-events-none',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-[#D7191F] text-white hover:bg-[#B5141A] disabled:bg-[#E2E4E7]',
        outline: [
          'border border-[#E2E4E7] bg-white text-[#0F1012] shadow-card-hover',
          'hover:border-[#0F1012] hover:bg-[#0F1012] hover:text-white',
          'disabled:text-[#9096A0] disabled:shadow-none',
        ],
      },
      size: {
        m: 'size-[52px]',
        s: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'outline',
      size: 'm',
    },
  },
);

export type IconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> &
  VariantProps<typeof iconButtonVariants> & {
    icon: IconName;
    /** Accessible name – the button has no visible text. */
    label: string;
    /** Icon size in px (Figma 24; the hover/disabled variants of the set show a 20 px caret). */
    iconSize?: number;
  };

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, iconSize = 24, variant, size, className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...props}
    >
      <Icon name={icon} size={iconSize} />
    </button>
  ),
);
IconButton.displayName = 'IconButton';

export { iconButtonVariants };
